import {useMemo} from 'react';
import translate from '@/utils/translate';
import PERMISSIONS from '@/config/permissions';
import {ROUTES} from '@/config/routesConstants';
import {CampaignProps} from '@/types/campaign';
import {CampaignTab} from '@/types/campaignTab';
import {CAMPAIGN_TAB_ID, CampaignTabId} from '@/types/campaignTabId';
import useDashboardUserPermissions from '@/hooks/use-dashboard-user-permissions';

interface CampaignTabTemplate {
  testID: string;
  id: CampaignTabId;
  pathSuffix: string;
  localeLabelKey: string;
  order: number;
  getShowUnreadIndicator?: (campaign: CampaignProps) => boolean;
}

const DEFAULT_TABS: CampaignTabTemplate[] = [
  {
    id: CAMPAIGN_TAB_ID.INVITES,
    pathSuffix: '/candidates',
    localeLabelKey: 'Invites',
    testID: 'invites',
    order: 3,
  },
  {
    id: CAMPAIGN_TAB_ID.PROPOSALS,
    pathSuffix: '/proposals',
    localeLabelKey: 'Applications',
    testID: 'proposals',
    order: 4,
    getShowUnreadIndicator: (campaign) =>
      (campaign?.proposalsCount ?? 0) > 0 ||
      (campaign?.unreadApplicationMessagesCount ?? 0) > 0,
  },
  {
    id: CAMPAIGN_TAB_ID.CONTRACTS,
    pathSuffix: '/contracts',
    localeLabelKey: 'Participants',
    testID: 'contracts',
    order: 7,
    getShowUnreadIndicator: (campaign) =>
      (campaign?.brandActionItemsGroup?.contentSubmissionsCount ?? 0) > 0 ||
      (campaign?.unreadParticipantMessagesCount ?? 0) > 0,
  },
  {
    id: CAMPAIGN_TAB_ID.DOWNLOADS,
    pathSuffix: '/content-downloads',
    localeLabelKey: 'Assets',
    testID: 'downloads',
    order: 8,
  },
  {
    id: CAMPAIGN_TAB_ID.STATS,
    pathSuffix: '/stats',
    localeLabelKey: 'Stats',
    testID: 'stats',
    order: 9,
  },
  {
    id: CAMPAIGN_TAB_ID.SETTINGS,
    pathSuffix: '/settings',
    localeLabelKey: 'Settings',
    testID: 'settings',
    order: 10,
  },
];

const CONDITIONAL_TABS: CampaignTabTemplate[] = [
  {
    id: CAMPAIGN_TAB_ID.BRIEF,
    pathSuffix: '/brief',
    localeLabelKey: 'Brief',
    testID: 'brief',
    order: 1,
  },
  {
    id: CAMPAIGN_TAB_ID.RECRUIT,
    pathSuffix: '/recruit',
    localeLabelKey: 'Recruit',
    testID: 'recruit',
    order: 2,
  },
  {
    id: CAMPAIGN_TAB_ID.FULFILLMENT,
    pathSuffix: '/fulfillment',
    localeLabelKey: 'Fulfillment',
    testID: 'fulfillment',
    order: 5,
  },
  {
    id: CAMPAIGN_TAB_ID.DISCOUNT_CODES,
    pathSuffix: '/discount-codes',
    localeLabelKey: 'Discount Codes',
    testID: 'discount-codes',
    order: 6,
  },
  {
    id: CAMPAIGN_TAB_ID.TIMELINE,
    pathSuffix: '/timeline',
    localeLabelKey: 'Timeline',
    testID: 'timeline',
    order: 11,
  },
];

interface UseCampaignTabsResult {
  tabs: CampaignTab[];
}

/**
 * Hook that determines which campaign tabs should be displayed
 * Returns tabs array with disabled property set for tabs that should not be clickable
 */
function useCampaignTabs(campaign: CampaignProps): UseCampaignTabsResult {
  const showDiscountCodesTab =
    !!campaign?.affiliateType && !!campaign?.brand?.hasShopifyAccessToken;

  const {viewRecruitsAgencyCreatorProfiles, viewCampaignTimeline} =
    useDashboardUserPermissions([
      PERMISSIONS.AGENCY_CREATOR_PROFILES.VIEW_RECRUITS,
      PERMISSIONS.CAMPAIGN_TIMELINE.VIEW,
    ]);

  const showCampaignDetails = campaign.showCampaignDetails ?? false;

  return useMemo(() => {
    if (!campaign?.id) {
      return {tabs: []};
    }

    const basePath = `${ROUTES.CAMPAIGNS}/${campaign.id}`;

    const createTab = (
      template: CampaignTabTemplate,
      disabled?: boolean
    ): CampaignTab => {
      const tab: CampaignTab = {
        id: template.id,
        path: `${basePath}${template.pathSuffix}`,
        label: translate(template.localeLabelKey),
        testID: template.testID,
        order: template.order,
      };

      if (disabled) {
        tab.disabled = true;
      }

      if (template.getShowUnreadIndicator) {
        tab.showUnreadIndicator = template.getShowUnreadIndicator(campaign);
      }

      return tab;
    };

    const tabs: CampaignTab[] = [];

    // Always include brief tab
    const briefTemplate = CONDITIONAL_TABS.find(
      (tab) => tab.id === CAMPAIGN_TAB_ID.BRIEF
    );
    if (!briefTemplate) {
      return {tabs: []};
    }
    tabs.push(createTab(briefTemplate, !showCampaignDetails));

    // If showCampaignDetails is false, only return the disabled brief tab
    if (!showCampaignDetails) {
      return {tabs};
    }

    // Add default tabs
    tabs.push(...DEFAULT_TABS.map((template) => createTab(template)));

    // Add conditional tabs (excluding brief which we already added)
    const shouldShowTab: Partial<Record<CampaignTabId, boolean>> = {
      [CAMPAIGN_TAB_ID.RECRUIT]: !!viewRecruitsAgencyCreatorProfiles,
      [CAMPAIGN_TAB_ID.FULFILLMENT]: !!campaign?.hasFulfillment,
      [CAMPAIGN_TAB_ID.DISCOUNT_CODES]: showDiscountCodesTab,
      [CAMPAIGN_TAB_ID.TIMELINE]: !!viewCampaignTimeline,
    };

    CONDITIONAL_TABS.forEach((template) => {
      if (template.id !== CAMPAIGN_TAB_ID.BRIEF && shouldShowTab[template.id]) {
        tabs.push(createTab(template));
      }
    });

    tabs.sort((a, b) => a.order - b.order);

    return {tabs};
  }, [
    campaign.id,
    showCampaignDetails,
    campaign.hasFulfillment,
    showDiscountCodesTab,
    campaign.proposalsCount,
    campaign.unreadApplicationMessagesCount,
    campaign.brandActionItemsGroup?.contentSubmissionsCount,
    campaign.unreadParticipantMessagesCount,
    viewRecruitsAgencyCreatorProfiles,
    viewCampaignTimeline,
  ]);
}

export default useCampaignTabs;
export type {UseCampaignTabsResult};
